/**
 * DeletionPending — full-page state for an account the owner scheduled for deletion.
 *
 * The API blocks every request except /v1/me and POST /v1/settings/cancel-deletion
 * while a deletion is pending, so this replaces the app. Only the owner sees "Cancel
 * deletion"; everyone else is told who can.
 */
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useMe } from '@/lib/hooks/use-me';
import { useToast } from '../ui';

/** Renders the app, or the pending page when /v1/me says the account is being deleted. */
export function DeletionGate({ children }: { children: ReactNode }) {
  const me = useMe();
  return me.deletionPending ? <DeletionPending scheduledFor={me.deletionPending.scheduledFor} isOwner={me.role === 'owner'} /> : <>{children}</>;
}

export function DeletionPending({ scheduledFor, isOwner }: { scheduledFor: string; isOwner: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    setBusy(true);
    try {
      await api('/v1/settings/cancel-deletion', { method: 'POST' });
      toast('Deletion canceled. Reconnect your integrations and resubscribe to resume.', 'success');
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not cancel the deletion.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6">
      <div className="w-full max-w-md rounded-2xl border border-danger/30 bg-surface-0 p-8 text-center">
        <h1 className="text-lg font-semibold text-ink">This account is scheduled for deletion</h1>
        <p className="mt-3 text-sm text-ink-muted">
          Your subscription has been canceled and all data will be permanently erased on{' '}
          <strong>{new Date(scheduledFor).toLocaleDateString()}</strong>.
        </p>
        {isOwner ? (
          <button
            onClick={cancel}
            disabled={busy}
            className="mt-6 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-bright disabled:opacity-50"
          >
            {busy ? 'Canceling…' : 'Cancel deletion'}
          </button>
        ) : (
          <p className="mt-6 text-sm text-ink-dim">Only the account owner can cancel the deletion.</p>
        )}
        <button
          onClick={() => { void supabase.auth.signOut().then(() => { window.location.href = '/login'; }); }}
          className="mt-3 text-2xs text-ink-dim hover:text-ink"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
